import React from "react";
import store from "helpers/store";
import {addInfoMessage} from "helpers/actions";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import FeatherIcon from "components/FeatherIcon";

export enum InfoMessageType {
    WARNING = "warning",
    INFO = "info",
    SUCCESS = "success",
    CLEARED = "cleared",
    ERROR = "error",
}

interface InfoMessageProps {
    message: string;
    messageType: InfoMessageType;
    children?: Element | React.ReactNode | React.ReactNode[] | null;
}

export const setInfoMessage = (message, messageType, children: Element | React.ReactNode | React.ReactNode[] | null = null) => {
    store.dispatch(addInfoMessage(message, messageType, children));
};

export const clearInfoMessage = () => {
    store.dispatch(addInfoMessage(null, InfoMessageType.CLEARED, null));
};

/**
 * Renders the InfoMessage component which is used to display informational, warning, error, or success messages
 * to the user in a modal-like interface. The component includes a title, message content, an icon representing
 * the message type, and a close button.
 *
 * The displayed message can be customized dynamically based on the `infoMessage` state from the Redux store.
 * The design, title, and icon of the message change depending on the message type (warning, info, error, success).
 *
 * Usage:
 * - The component listens to changes in the `infoMessage` state and conditionally renders the UI.
 * - The `closeMessageHandler` function clears the message when the modal or close button is clicked.
 * - Styling is applied dynamically based on the message type using helper functions (`getStatusColor`, `getTitle`, `getIcon`).
 *
 * Features:
 * - Type-specific designs for warning, info, error, and success messages.
 * - Includes a localized title and a button label using the `useTranslation` hook.
 * - Fully responsive layout and design.
 * - Self-closing functionality to clear messages.
 *
 * Dependencies:
 * - React hooks such as `useEffect` are used for lifecycle management.
 * - Custom hooks like `useTypedSelector` provide access to the `infoMessage` state.
 * - External components/libraries for localization (`useTranslation`) and icons (`FeatherIcon`).
 */
const InfoMessage = () => {
    const {t} = useTranslation();
    const infoMessage = useTypedSelector(state => state.infoMessage);

    if (!infoMessage || !infoMessage.message) {
        return null;
    }
    const {message, messageType, children} = infoMessage;
    const closeMessageHandler = () => {
        clearInfoMessage();
    };

    const getStatusColor = () => {
        switch (messageType) {
            case InfoMessageType.WARNING:
                return "text-yellow-800 border-yellow-200";
            case InfoMessageType.INFO:
                return "text-blue-800 border-blue-200";
            case InfoMessageType.SUCCESS:
                return "text-green-800 border-green-200";
            case InfoMessageType.ERROR:
                return "!bg-red-50 text-red-800 border-red-200";
        }
    };

    const getTitle = () => {
        switch (messageType) {
            case InfoMessageType.WARNING:
                return t("Warning");
            case InfoMessageType.INFO:
                return t("Info");
            case InfoMessageType.SUCCESS:
                return t("Success");
            case InfoMessageType.ERROR:
                return t("Error");
        }
    };
    const getIcon = () => {
        const className = " size-8 md:size-14 flex-none ";
        switch (messageType) {
            case InfoMessageType.WARNING:
                return <FeatherIcon name={"alert-circle"} className={className} strokeWidth={2} />;

            case InfoMessageType.INFO:
                return <FeatherIcon name={"info"} className={className} strokeWidth={2} />;

            case InfoMessageType.SUCCESS:
                return <FeatherIcon name={"check-circle"} className={className} strokeWidth={2} />;

            default:
            case InfoMessageType.ERROR:
                return <FeatherIcon name={"alert-circle"} className={className} strokeWidth={2} />;
        }
    };

    return (
        <main className={"fixed inset-0 z-[50000] flex items-center justify-center p-6"}>
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-black opacity-80"></div>
            </div>
            <div
                className={`InfoMessage z-50 flex flex-col gap-y-4 md:gap-y-16 items-center p-4 md:py-8 md:px-6 min-w-sm md:min-w-lg min-h-56 mb-4 text-sm md:text-lg rounded-[20px] cursor-pointer bg-white ${getStatusColor()}`}
                onClick={closeMessageHandler}
                role="alert"
            >
                <div className="shrink-0 inline-flex items-center justify-center">{getIcon()}</div>
                <h3 className={"MessageTitle font-zuume text-6xl font-bold"}>{getTitle()}</h3>
                {children}
                <div className={"text-xs md:text-lg font-semibold text-black whitespace-pre max-w-lg"}>{message.data ? message.data.message : message}</div>

                <button onClick={closeMessageHandler} role={"button"} type={"button"} className={"w-full max-w-xs z-10 cursor-pointer"}>
                    <div className={"border border-arbo-red font-bold rounded-[10px] text-black flex flex-row items-center justify-center px-4 py-3"}>
                        {t("Close")}
                    </div>
                </button>
            </div>
        </main>
    );
};

export default InfoMessage;
